/**
 * Type-Safe API Client
 *
 * Provides a fully typed wrapper around fetch API with:
 * - Automatic token injection
 * - Token refresh on 401
 * - Request/response interceptors
 * - Timeout handling
 * - Type-safe endpoints
 */

import type {
  User,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  RefreshTokenResponse,
  Project,
  CreateProjectRequest,
  UpdateProjectRequest,
  UploadFileResponse,
  Budget,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  SinapiSearchRequest,
  SinapiSearchResponse,
  SinapiCompositionDetail,
  ProjectStatusResponse,
  ApiError as ApiErrorType,
  Expense,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  FinancialSummary,
} from '@/types/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
const DEFAULT_TIMEOUT = 30000; // 30s

// ============================================================================
// ERROR HANDLING
// ============================================================================

export class ApiError extends Error implements ApiErrorType {
  constructor(
    public statusCode: number,
    message: string,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.timestamp = new Date().toISOString();
  }

  timestamp: string;
}

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

class TokenManager {
  private static TOKEN_KEY = 'token';
  private static REFRESH_TOKEN_KEY = 'refreshToken';

  // Helper to set cookie
  private static setCookie(name: string, value: string, days: number = 7): void {
    if (typeof window === 'undefined') return;
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  // Helper to get cookie
  private static getCookie(name: string): string | null {
    if (typeof window === 'undefined') return null;
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Helper to delete cookie
  private static deleteCookie(name: string): void {
    if (typeof window === 'undefined') return;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }

  static getToken(): string | null {
    if (typeof window === 'undefined') return null;
    // Try cookie first (for middleware), then localStorage (fallback)
    return this.getCookie(this.TOKEN_KEY) || localStorage.getItem(this.TOKEN_KEY);
  }

  static setToken(token: string): void {
    if (typeof window !== 'undefined') {
      // Save to both cookie (for middleware) and localStorage (for client)
      localStorage.setItem(this.TOKEN_KEY, token);
      this.setCookie(this.TOKEN_KEY, token, 7); // 7 days
    }
  }

  static getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return this.getCookie(this.REFRESH_TOKEN_KEY) || localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static setRefreshToken(token: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
      this.setCookie(this.REFRESH_TOKEN_KEY, token, 30); // 30 days for refresh token
    }
  }

  static clearTokens(): void {
    if (typeof window !== 'undefined') {
      // Clear both localStorage and cookies
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      this.deleteCookie(this.TOKEN_KEY);
      this.deleteCookie(this.REFRESH_TOKEN_KEY);
    }
  }
}

// ============================================================================
// REFRESH TOKEN QUEUE
// ============================================================================

interface QueueItem<T = unknown> {
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason: unknown) => void;
}

class RefreshQueue {
  private isRefreshing = false;
  private queue: QueueItem[] = [];

  async add<T>(callback: () => Promise<T>): Promise<T> {
    if (!this.isRefreshing) {
      return callback();
    }

    return new Promise<T>((resolve, reject) => {
      this.queue.push({ resolve: resolve as any, reject });
    });
  }

  setRefreshing(value: boolean): void {
    this.isRefreshing = value;
  }

  processQueue(error: Error | null, token: string | null = null): void {
    this.queue.forEach((item) => {
      if (error) {
        item.reject(error);
      } else {
        item.resolve(token);
      }
    });
    this.queue = [];
  }
}

const refreshQueue = new RefreshQueue();

// ============================================================================
// HTTP CLIENT
// ============================================================================

interface FetchOptions extends RequestInit {
  timeout?: number;
  skipAuth?: boolean;
  skipRefresh?: boolean;
}

async function fetchWithTimeout(
  url: string,
  options: FetchOptions = {}
): Promise<Response> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      throw new ApiError(
        408,
        `Requisição excedeu o tempo limite de ${timeout / 1000}s`,
        'TIMEOUT'
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = TokenManager.getRefreshToken();

  if (!refreshToken) {
    throw new ApiError(401, 'No refresh token available', 'NO_REFRESH_TOKEN');
  }

  try {
    const response = await fetchWithTimeout(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      skipAuth: true,
      skipRefresh: true,
    });

    if (!response.ok) {
      throw new ApiError(
        response.status,
        'Failed to refresh token',
        'REFRESH_FAILED'
      );
    }

    const data: RefreshTokenResponse = await response.json();
    TokenManager.setToken(data.accessToken);

    if (data.refreshToken) {
      TokenManager.setRefreshToken(data.refreshToken);
    }

    return data.accessToken;
  } catch (error) {
    // Only clear tokens for auth-related failures (401/403), not network errors
    if (error instanceof ApiError && (error.statusCode === 401 || error.statusCode === 403)) {
      TokenManager.clearTokens();
      if (typeof window !== 'undefined') {
        window.location.href = '/auth';
      }
    }
    // For network errors or server restarts, keep tokens and let user retry
    throw error;
  }
}

export async function fetchAPI<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  // Prepare headers
  const headers: Record<string, string> = {};

  // Copy existing headers
  if (options.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (Array.isArray(options.headers)) {
      options.headers.forEach(([key, value]) => {
        headers[key] = value;
      });
    } else {
      Object.assign(headers, options.headers);
    }
  }

  // Add Authorization header if not skipped
  if (!options.skipAuth) {
    const token = TokenManager.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Add Content-Type if not FormData
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    const response = await fetchWithTimeout(url, {
      ...options,
      headers,
    });

    // Handle 401 with token refresh
    if (response.status === 401 && !options.skipRefresh) {
      return refreshQueue.add(async () => {
        refreshQueue.setRefreshing(true);

        try {
          const newToken = await refreshAccessToken();
          refreshQueue.processQueue(null, newToken);

          // Retry original request with new token
          const retryHeaders: Record<string, string> = {
            ...headers,
            'Authorization': `Bearer ${newToken}`,
          };

          const retryResponse = await fetchWithTimeout(url, {
            ...options,
            headers: retryHeaders,
          });

          if (!retryResponse.ok) {
            const errorData = await retryResponse.json().catch(() => ({}));
            throw new ApiError(
              retryResponse.status,
              errorData.detail || errorData.message || 'Request failed after token refresh',
              errorData.code
            );
          }

          return retryResponse.json();
        } catch (error) {
          refreshQueue.processQueue(error as Error);
          throw error;
        } finally {
          refreshQueue.setRefreshing(false);
        }
      });
    }

    // Handle other error responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message:
          response.status === 404
            ? 'Endpoint não encontrado'
            : 'Erro na requisição',
      }));

      throw new ApiError(
        response.status,
        errorData.detail || errorData.message || 'Request failed',
        errorData.code,
        errorData.details
      );
    }

    // Parse successful response
    return response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Network or other errors
    throw new ApiError(
      0,
      'Não foi possível conectar ao servidor. Verifique sua conexão.',
      'NETWORK_ERROR',
      error
    );
  }
}

// ============================================================================
// AUTH API
// ============================================================================

export const authAPI = {
  register: (data: RegisterRequest): Promise<AuthResponse> =>
    fetchAPI('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
      skipRefresh: true,
    }),

  login: (data: LoginRequest): Promise<AuthResponse> =>
    fetchAPI('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
      skipRefresh: true,
    }),

  googleLogin: (credential: string): Promise<AuthResponse> =>
    fetchAPI('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
      skipAuth: true,
      skipRefresh: true,
    }),

  logout: (): Promise<void> =>
    fetchAPI('/auth/logout', {
      method: 'POST',
    }),

  getProfile: (): Promise<User> => fetchAPI('/auth/me'),

  refreshToken: (): Promise<RefreshTokenResponse> =>
    fetchAPI('/auth/refresh', {
      method: 'POST',
      skipRefresh: true,
    }),
};

// ============================================================================
// PROJECT API
// ============================================================================

export const projectAPI = {
  getAll: (): Promise<Project[]> => fetchAPI('/projects'),

  getOne: (id: string): Promise<Project> => fetchAPI(`/projects/${id}`),

  create: (data: CreateProjectRequest): Promise<Project> =>
    fetchAPI('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: UpdateProjectRequest): Promise<Project> =>
    fetchAPI(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string): Promise<void> =>
    fetchAPI(`/projects/${id}`, {
      method: 'DELETE',
    }),

  /**
   * Upload floorplan file to existing project
   * Triggers async processing (Bull Queue)
   *
   * @param id - Project ID
   * @param file - Floorplan file (image/PDF)
   * @returns Upload confirmation with processingId
   */
  upload: (
    id: string,
    file: File
  ): Promise<{ message: string; processingId: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    return fetchAPI(`/projects/${id}/upload`, {
      method: 'POST',
      body: formData,
      timeout: 60000, // 60s for upload
    });
  },

  /**
   * Get project with full status and budget/schedule if completed
   * Use for polling processing status
   *
   * @param id - Project ID
   * @returns Project with status, budget, schedule
   */
  getStatus: (id: string): Promise<ProjectStatusResponse> =>
    fetchAPI(`/projects/${id}/status`),
};

// ============================================================================
// BUDGET API
// ============================================================================

export const budgetAPI = {
  getByProject: (projectId: string): Promise<Budget> =>
    fetchAPI(`/projects/${projectId}/budget`),

  getOne: (id: string): Promise<Budget> =>
    fetchAPI(`/projects/${id}/budget`),

  // List all budgets across all projects
  getAll: async (): Promise<Budget[]> => {
    const projects = await fetchAPI<Project[]>('/projects');
    const budgets: Budget[] = [];
    for (const project of projects) {
      try {
        const budget = await fetchAPI<Budget>(`/projects/${project.id}/budget`);
        if (budget) budgets.push(budget);
      } catch {
        // Project may not have a budget yet
      }
    }
    return budgets;
  },

  create: (data: { projectId: string }): Promise<Budget> =>
    fetchAPI(`/projects/${data.projectId}/budget`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Record<string, unknown>): Promise<Budget> =>
    fetchAPI(`/projects/${id}/budget`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),

  delete: (id: string): Promise<void> =>
    fetchAPI(`/projects/${id}/budget`, {
      method: 'DELETE',
    }),

  upload: (
    file: File,
    data: {
      projectName: string;
      clientName?: string;
      notes?: string;
    }
  ): Promise<UploadFileResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectName', data.projectName);
    if (data.clientName) formData.append('clientName', data.clientName);
    if (data.notes) formData.append('notes', data.notes);

    return fetchAPI('/budget/upload', {
      method: 'POST',
      body: formData,
    });
  },
};

// ============================================================================
// SINAPI API
// ============================================================================

export const sinapiAPI = {
  /**
   * Busca insumos SINAPI (materiais básicos)
   */
  searchInsumos: (params: {
    uf: string
    term?: string
    mes?: number
    ano?: number
    page?: number
    limit?: number
  }): Promise<any> => {
    const queryParams = new URLSearchParams();
    queryParams.set('uf', params.uf);
    if (params.term) queryParams.set('term', params.term);
    if (params.mes) queryParams.set('mes', params.mes.toString());
    if (params.ano) queryParams.set('ano', params.ano.toString());
    if (params.page !== undefined) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());

    return fetchAPI(`/sinapi/insumos?${queryParams.toString()}`);
  },

  /**
   * Busca composições SINAPI (serviços completos)
   */
  searchComposicoes: (params: {
    uf: string
    term?: string
    mes?: number
    ano?: number
    page?: number
    limit?: number
  }): Promise<any> => {
    const queryParams = new URLSearchParams();
    queryParams.set('uf', params.uf);
    if (params.term) queryParams.set('term', params.term);
    if (params.mes) queryParams.set('mes', params.mes.toString());
    if (params.ano) queryParams.set('ano', params.ano.toString());
    if (params.page !== undefined) queryParams.set('page', params.page.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());

    return fetchAPI(`/sinapi/composicoes?${queryParams.toString()}`);
  },

  /**
   * Busca insumo específico por código
   */
  getInsumo: (codigo: string, uf: string, mes?: number, ano?: number): Promise<any> => {
    const queryParams = new URLSearchParams();
    queryParams.set('uf', uf);
    if (mes) queryParams.set('mes', mes.toString());
    if (ano) queryParams.set('ano', ano.toString());

    return fetchAPI(`/sinapi/insumo/${codigo}?${queryParams.toString()}`);
  },

  /**
   * Busca composição específica por código
   */
  getComposicao: (codigo: string, uf: string, mes?: number, ano?: number): Promise<any> => {
    const queryParams = new URLSearchParams();
    queryParams.set('uf', uf);
    if (mes) queryParams.set('mes', mes.toString());
    if (ano) queryParams.set('ano', ano.toString());

    return fetchAPI(`/sinapi/composicao/${codigo}?${queryParams.toString()}`);
  },

  /**
   * Lista estados brasileiros disponíveis
   */
  getEstados: (): Promise<any> => fetchAPI('/sinapi/estados'),

  getStats: (): Promise<any> => fetchAPI('/sinapi/stats'),

  /**
   * Lista anos disponíveis para uma UF
   */
  getAnosDisponiveis: (uf: string): Promise<any> =>
    fetchAPI(`/sinapi/anos-disponiveis/${uf}`),

  /**
   * Lista meses disponíveis para uma UF e ano
   */
  getMesesDisponiveis: (uf: string, ano: number): Promise<any> =>
    fetchAPI(`/sinapi/meses-disponiveis/${uf}/${ano}`),

  // Legacy methods (mantidos para compatibilidade)
  search: (params: SinapiSearchRequest): Promise<SinapiSearchResponse> => {
    const queryParams = new URLSearchParams();
    queryParams.set('query', params.query);
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.offset) queryParams.set('offset', params.offset.toString());
    if (params.categoria) queryParams.set('categoria', params.categoria);
    if (params.tipo) queryParams.set('tipo', params.tipo);

    return fetchAPI(`/sinapi/search?${queryParams.toString()}`);
  },

  getComposition: (codigo: string): Promise<SinapiCompositionDetail> =>
    fetchAPI(`/sinapi/compositions/${codigo}`),

  comparePrices: (data: {
    codigosSinapi: string[]
    ufs: string[]
    versao?: 'desonerada' | 'nao_desonerada'
  }): Promise<any> =>
    fetchAPI('/sinapi/compare-prices', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ============================================================================
// EXPENSE API (Gestão Financeira)
// ============================================================================

export const expenseAPI = {
  /**
   * Lista todas as despesas do usuário
   */
  getAll: (params?: {
    projectId?: string;
    categoria?: string;
    status?: string;
    dataInicio?: string;
    dataFim?: string;
  }): Promise<Expense[]> => {
    const queryParams = new URLSearchParams();
    if (params?.projectId) queryParams.set('projectId', params.projectId);
    if (params?.categoria) queryParams.set('categoria', params.categoria);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.dataInicio) queryParams.set('dataInicio', params.dataInicio);
    if (params?.dataFim) queryParams.set('dataFim', params.dataFim);

    const query = queryParams.toString();
    return fetchAPI(query ? `/expenses?${query}` : '/expenses');
  },

  /**
   * Busca uma despesa específica
   */
  getOne: (id: string): Promise<Expense> => fetchAPI(`/expenses/${id}`),

  /**
   * Cria nova despesa
   */
  create: (data: CreateExpenseRequest): Promise<Expense> =>
    fetchAPI('/expenses', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Atualiza despesa existente
   */
  update: (id: string, data: UpdateExpenseRequest): Promise<Expense> =>
    fetchAPI(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * Deleta despesa
   */
  delete: (id: string): Promise<void> =>
    fetchAPI(`/expenses/${id}`, {
      method: 'DELETE',
    }),

  /**
   * Faz upload de comprovante de despesa
   */
  uploadComprovante: (id: string, file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    return fetchAPI(`/expenses/${id}/comprovante`, {
      method: 'POST',
      body: formData,
    });
  },

  /**
   * Busca resumo financeiro (orçado vs realizado)
   */
  getSummary: (projectId?: string): Promise<FinancialSummary> => {
    const query = projectId ? `?projectId=${projectId}` : '';
    return fetchAPI(`/expenses/summary${query}`);
  },
};

// ============================================================================
// GENERIC API CLIENT
// ============================================================================

export const api = {
  get: <T = unknown>(endpoint: string, options?: FetchOptions): Promise<{ data: T }> =>
    fetchAPI<T>(endpoint, options).then((data) => ({ data })),

  post: <T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: FetchOptions
  ): Promise<{ data: T }> =>
    fetchAPI<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }).then((data) => ({ data })),

  put: <T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: FetchOptions
  ): Promise<{ data: T }> =>
    fetchAPI<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }).then((data) => ({ data })),

  patch: <T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: FetchOptions
  ): Promise<{ data: T }> =>
    fetchAPI<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }).then((data) => ({ data })),

  delete: <T = unknown>(endpoint: string, options?: FetchOptions): Promise<{ data: T }> =>
    fetchAPI<T>(endpoint, { ...options, method: 'DELETE' }).then((data) => ({ data })),
};

// ============================================================================
// EXPORTS
// ============================================================================

export { API_URL, TokenManager };
export type { FetchOptions };
